
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';

export const useProviderQuotes = (providerId: string) => {
  const [debugInfo, setDebugInfo] = useState({
    hasRun: false,
    userId: null,
    providerId: providerId,
    timestamp: new Date().toISOString(),
    error: null,
    lastResponse: null,
  });

  const fetchProviderQuotes = async () => {
    console.log(`Fetching quotes for provider ${providerId}`);
    
    try {
      // First, let's check if the user is authenticated
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('Auth error when fetching quotes:', authError);
        setDebugInfo(prev => ({ ...prev, error: authError.message, userId: null }));
        throw new Error(`Authentication error: ${authError.message}`);
      }
      
      if (!user) {
        console.error('No authenticated user found when fetching quotes');
        setDebugInfo(prev => ({ ...prev, error: 'No authenticated user', userId: null }));
        throw new Error('No authenticated user found');
      }
      
      console.log(`Authenticated as user ${user.id}, fetching quotes for provider ${providerId}`);
      setDebugInfo(prev => ({ ...prev, userId: user.id, hasRun: true }));
      
      // Query quotes with full joins for application data
      const { data, error } = await supabase
        .from('peo_quotes')
        .select(`
          *,
          application:application_id (
            id,
            company_legal_name,
            company_dba,
            primary_industry,
            first_name,
            last_name
          )
        `)
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching provider quotes:', error);
        setDebugInfo(prev => ({ ...prev, error: error.message }));
        throw new Error(`Failed to fetch quotes: ${error.message}`);
      }
      
      // Store the response in debug info for inspection
      setDebugInfo(prev => ({ ...prev, lastResponse: data }));
      
      console.log(`Successfully fetched ${data?.length || 0} quotes:`, data);
      
      // Enhanced logging for each quote's data
      data?.forEach((quote, index) => {
        console.log(`Quote #${index + 1} (ID: ${quote.id}):`);
        console.log(`- Monthly Cost: ${quote.monthly_cost} (${typeof quote.monthly_cost})`);
        console.log(`- Annual Cost: ${quote.annual_cost} (${typeof quote.annual_cost})`);
        console.log(`- Valid Until: ${quote.valid_until}`);
        console.log(`- Application: ${quote.application?.company_legal_name || 'Unknown'}`);
        console.log(`- Status: ${quote.status}`);
        console.log(`- Has file URL: ${!!quote.quote_file_url}`);
        console.log(`- Is Submitted: ${quote.is_submitted}`);
      });
      
      return data;
    } catch (error: any) {
      console.error('Unexpected error in useProviderQuotes:', error);
      setDebugInfo(prev => ({ ...prev, error: error.message }));
      throw error;
    }
  };

  const queryResult = useQuery({
    queryKey: ['provider-quotes', providerId],
    queryFn: fetchProviderQuotes,
    refetchOnMount: true,
    staleTime: 30000 // Consider data stale after 30 seconds
  });

  return {
    ...queryResult,
    debugInfo
  };
};
